import {useCallback} from 'react';
import {List} from 'immutable';

import {addGenericErrorToast} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1} from '@albert-io/models';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {
  masqueradeSchoolFilter,
  readMasqueradeScope
} from 'client/generic/Masquerade/masqueradeScope';

import {useTeacherDashboardStore} from '../useTeacherDashboardStore';

/** Every caller builds this outside render (fetch callbacks, the debounced search), so the
 *  masquerade scope is read straight from the store rather than bridged with `useFluxStore`. */
export const getTeacherSidemenuClassroomsQuery = (
  status: 'active' | 'archived',
  nameSearch = ''
) => {
  const {teacherId, schoolId} = readMasqueradeScope();
  return (
    resource('classrooms_v1')
      .mandarkEndpoint(['teachers_v1', teacherId, 'classrooms_v1'])
      .include('subjects_v2.curriculum_area_v1')
      // Student/invitation counts feed the empty-class People default — same meta the legacy
      // /classes grid loaded per class.
      .withMeta('classroom_v1')
      .customQuery({meta: {context: {classroom: {counts: true}}}})
      .filter({status, ...masqueradeSchoolFilter(schoolId)})
      // Second arg gates the filter so an empty term returns the full list.
      .filter({name: {case_insensitive_substring: nameSearch}}, !!nameSearch)
      .sort('name')
      .pageSize(100)
  );
};

/**
 * Fetch actions only — subscribes to the zustand setters (stable refs) but NOT the classroom lists,
 * so action-only callers (create-class, Clever sync, settings mutations) don't re-render when a list
 * is replaced. Fetchers are `useCallback`-stable so they're safe in effect dep arrays.
 */
export const useTeacherClassroomActions = () => {
  const setArchivedClassrooms = useTeacherDashboardStore((state) => state.setArchivedClassrooms);
  const setAreArchivedClassroomsLoading = useTeacherDashboardStore(
    (state) => state.setAreArchivedClassroomsLoading
  );
  const setArchivedClassroomsLoadedFor = useTeacherDashboardStore(
    (state) => state.setArchivedClassroomsLoadedFor
  );
  const setAreClassroomsLoading = useTeacherDashboardStore(
    (state) => state.setAreClassroomsLoading
  );
  const setHasLoadedClassrooms = useTeacherDashboardStore((state) => state.setHasLoadedClassrooms);
  const setClassrooms = useTeacherDashboardStore((state) => state.setClassrooms);

  const fetchWith = useCallback(
    (
      status: 'active' | 'archived',
      setLoading: (isLoading: boolean) => void,
      setResults: (results: List<ClassroomModelV1>) => void
    ) => {
      setLoading(true);

      // Built per fetch so masquerade identity switches are picked up.
      const query = getTeacherSidemenuClassroomsQuery(status);
      query.invalidateInterest();

      query
        .getResourcePromise()
        .then((fetchedClassrooms) => {
          // The legacy request builder is untyped; this endpoint returns classrooms.
          setResults(fetchedClassrooms as List<ClassroomModelV1>);
        })
        .catch(() => {
          setResults(List([]));
          addGenericErrorToast();
        })
        .finally(() => {
          setLoading(false);
        });
    },
    []
  );

  const fetchClassrooms = useCallback(() => {
    // Mark loaded once the fetch settles (success or error, since both call setResults) so Home can
    // distinguish "not fetched yet" from "genuinely no classes".
    fetchWith('active', setAreClassroomsLoading, (results) => {
      setClassrooms(results);
      setHasLoadedClassrooms(true);
    });
  }, [fetchWith, setAreClassroomsLoading, setClassrooms, setHasLoadedClassrooms]);

  const fetchArchivedClassrooms = useCallback(() => {
    // Stamp WHICH identity the list was loaded for once the fetch settles (success or error) so Home can
    // gate its first paint on the archived list during auto-archive season (#19169) and re-fetch after a
    // masquerade switch. Captured up-front so a mid-flight identity change stamps who we actually fetched.
    const loadedForUserId = masqueradeStore.getUserIdByMasqueradeState() ?? '';
    fetchWith('archived', setAreArchivedClassroomsLoading, (results) => {
      setArchivedClassrooms(results);
      setArchivedClassroomsLoadedFor(loadedForUserId);
    });
  }, [
    fetchWith,
    setAreArchivedClassroomsLoading,
    setArchivedClassrooms,
    setArchivedClassroomsLoadedFor
  ]);

  return {fetchArchivedClassrooms, fetchClassrooms};
};

export const useTeacherClassrooms = () => {
  const archivedClassrooms = useTeacherDashboardStore((state) => state.archivedClassrooms);
  const areArchivedClassroomsLoading = useTeacherDashboardStore(
    (state) => state.areArchivedClassroomsLoading
  );
  const archivedClassroomsLoadedFor = useTeacherDashboardStore(
    (state) => state.archivedClassroomsLoadedFor
  );
  const areClassroomsLoading = useTeacherDashboardStore((state) => state.areClassroomsLoading);
  const hasLoadedClassrooms = useTeacherDashboardStore((state) => state.hasLoadedClassrooms);
  const classrooms = useTeacherDashboardStore((state) => state.classrooms);
  const {fetchArchivedClassrooms, fetchClassrooms} = useTeacherClassroomActions();

  return {
    archivedClassrooms,
    areArchivedClassroomsLoading,
    archivedClassroomsLoadedFor,
    areClassroomsLoading,
    hasLoadedClassrooms,
    classrooms,
    fetchArchivedClassrooms,
    fetchClassrooms
  };
};
